/**
 * Vue Kanban : colonnes en cellules, glisser-déposer natif HTML5 avec mise à jour
 * optimiste, et alternative clavier complète (le glisser-déposer seul n'est pas
 * accessible — design system §11).
 *
 * Grammaire planche B (exiUI 1.5.0, reference.md §11.7, décision du 2026-09-28) : chaque
 * colonne est une cellule FERMÉE (`CELL_PLANCHE`, alias de `.cell`) — cadre 2 px complet et ses
 * quatre équerres `.ticks` — séparée de la suivante par la gouttière de 16 px de `.cells`
 * (`CELLS_PLANCHE`), qui appartient au paquet. Le fond de colonne et de carte est `bg-paper`
 * (plus de gris `--tray`/`--tray-item`). Les cartes d'une même colonne restent empilées pleine
 * largeur, séparées par un trait 2 px (`divide-y-2 divide-line`), sans gap ni marge : c'est une
 * LISTE dans l'objet colonne (comme les lignes d'un tableau dans une cellule), pas des cellules
 * de planche — elles n'ont ni `.cell` ni équerres, et ne partagent aucun trait avec les colonnes
 * voisines. La hachure est réservée à la colonne vide (règle « hachure = vide », reference.md
 * §2.6) : elle a quitté l'en-tête.
 * Le fond ne porte jamais seul l'information : la carte déplaçable garde son rail de
 * prise et son curseur `grab` ; la cible de dépose (colonne survolée ou carte saisie au
 * clavier) passe en `bg-accent-wash`, doublé du libellé « Déplacer vers… » sous la carte
 * saisie (jamais la couleur seule). Une bordure ou une ombre dédiées auraient doublé le cadre
 * de la colonne — proscrit par la gate `check-design`.
 *
 * Important pour le clavier : tant que la dépose (Entrée) n'est pas commise, la carte
 * ne change PAS de colonne dans le DOM — seule une cible de prévisualisation est
 * mémorisée (`previewColumnId`). La déplacer réellement à chaque flèche réordonnerait
 * l'arbre React (la carte change de section parente) et lui ferait perdre le focus au
 * milieu de l'interaction clavier, ce qui casserait justement l'alternative accessible.
 */

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { useAsync } from '../hooks/useAsync.js';
import { useAllTasks } from '../hooks/useAllTasks.js';
import { appAlert } from '../dialogs/service.js';
import {
  Empty,
  LoadError,
  PriorityTag,
  Progress,
  StatusTag,
  TruncatedTasksWarning,
  taskReference,
} from '../components/common.jsx';
import { BTN_ICON, CELLS_PLANCHE, CELL_PLANCHE } from '../ui/theme.js';

/** Regroupe les tâches par colonne, triées par `position`. */
function groupByColumn(tasks) {
  const byColumn = new Map();
  for (const task of tasks) {
    const list = byColumn.get(task.columnId) ?? [];
    list.push(task);
    byColumn.set(task.columnId, list);
  }
  for (const list of byColumn.values()) list.sort((a, b) => a.position - b.position);
  return byColumn;
}

/**
 * @param {object} props
 * @param {((columnId: string) => void)|null} [props.onCreateTask] Ouvre la création de tâche
 *   préremplie sur une colonne. `null` quand l'appelant sait que le serveur refuserait
 *   (rôle insuffisant, projet archivé) : aucune amorce n'est alors affichée.
 * @param {boolean} [props.canMove] Droit de déplacer une tâche. Défaut `false` — fermé par
 *   défaut : une prop oubliée doit retirer l'affordance, pas en créer une fausse que le
 *   serveur refusera ensuite.
 */
export function KanbanView({
  projectId,
  projectKey,
  onOpenTask,
  onCreateTask,
  canMove = false,
  reloadKey,
}) {
  const { t } = useTranslation();
  const [pickedTaskId, setPickedTaskId] = useState(null);
  const [previewColumnId, setPreviewColumnId] = useState(null);

  const {
    data: columns,
    error: columnsError,
    reload: reloadColumns,
  } = useAsync(
    (signal) => api.get(`/projects/${projectId}/columns`, { signal }).then((r) => r.data ?? []),
    [projectId, reloadKey],
  );
  // Toutes les pages sont chargées (§ issue B5) : un kanban qui n'en montrait que 200 laissait
  // des cartes invisibles, sans le moindre indicateur, au-delà de ce compte.
  const {
    tasks: tasksPage,
    truncated,
    error: tasksError,
    patch,
    reload,
  } = useAllTasks(projectId, {
    deps: [reloadKey],
  });

  const tasks = tasksPage ?? [];
  /*
   * Les lots ne vivent pas sur le kanban (décision D1) : leurs dates et leur avancement
   * viendront de leurs enfants, et une carte qu'on ne peut pas faire avancer soi-même n'a
   * rien à faire dans une colonne d'exécution. Le lot reste lisible autrement — chaque carte
   * porte son nom en surtitre.
   */
  const phaseNameById = useMemo(
    () =>
      new Map(tasks.filter((task) => task.kind === 'phase').map((task) => [task.id, task.title])),
    [tasks],
  );
  const boardTasks = useMemo(() => tasks.filter((task) => task.kind !== 'phase'), [tasks]);
  const grouped = useMemo(() => groupByColumn(boardTasks), [boardTasks]);
  const columnNameById = useMemo(
    () => new Map((columns ?? []).map((c) => [c.id, c.name])),
    [columns],
  );

  const cancelPick = useCallback(() => {
    setPickedTaskId(null);
    setPreviewColumnId(null);
  }, []);

  const moveTask = useCallback(
    async (taskId, columnId, beforeTaskId, afterTaskId) => {
      const previous = tasks;
      // La colonne d'arrivée pilote le statut côté serveur (`moveTask`). L'affichage
      // optimiste doit le refléter, sinon la carte saute de colonne en gardant son ancien
      // statut jusqu'au retour du serveur — l'image même d'un statut qui ne suit pas.
      const statutCible = (columns ?? []).find((c) => c.id === columnId)?.status ?? null;
      patch((current) =>
        (current ?? []).map((t) =>
          t.id === taskId ? { ...t, columnId, status: statutCible ?? t.status } : t,
        ),
      );
      try {
        await api.post(`/projects/${projectId}/tasks/${taskId}/move`, {
          columnId,
          beforeTaskId,
          afterTaskId,
        });
        reload();
      } catch (error) {
        patch(() => previous);
        await appAlert(t('kanban.moveFailed', { message: error.message }));
      }
    },
    [projectId, patch, reload, tasks, columns, t],
  );

  const onDrop = useCallback(
    (columnId) => (event) => {
      event.preventDefault();
      const taskId = event.dataTransfer.getData('text/plain');
      if (taskId) moveTask(taskId, columnId);
    },
    [moveTask],
  );

  /** Clavier : Espace saisit la carte (mémorise la colonne cible), flèches gauche/droite
   * déplacent la cible sans bouger la carte, Entrée commit le déplacement, Échap annule. */
  const onCardKeyDown = useCallback(
    (task) => (event) => {
      if (!canMove) return;
      const isPicked = pickedTaskId === task.id;

      if (event.key === ' ') {
        event.preventDefault();
        if (!isPicked) {
          setPickedTaskId(task.id);
          setPreviewColumnId(task.columnId);
        }
        return;
      }
      if (!isPicked) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        cancelPick();
        return;
      }
      if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && columns) {
        event.preventDefault();
        const ids = columns.map((c) => c.id);
        const index = ids.indexOf(previewColumnId);
        const nextIndex = event.key === 'ArrowLeft' ? index - 1 : index + 1;
        if (nextIndex >= 0 && nextIndex < ids.length) setPreviewColumnId(ids[nextIndex]);
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        const targetColumnId = previewColumnId;
        cancelPick();
        if (targetColumnId && targetColumnId !== task.columnId) moveTask(task.id, targetColumnId);
      }
    },
    [canMove, pickedTaskId, previewColumnId, columns, cancelPick, moveTask],
  );

  const loadError = columnsError ?? tasksError;
  if (loadError) {
    return (
      <LoadError
        error={loadError}
        onRetry={() => {
          if (columnsError) reloadColumns();
          if (tasksError) reload();
        }}
      />
    );
  }
  if (!columns) return null;
  if (columns.length === 0) {
    return <Empty>{t('kanban.empty')}</Empty>;
  }

  return (
    <div className="flex flex-col gap-2">
      {truncated && <TruncatedTasksWarning />}
      {/* Défilement horizontal posé sur la grille elle-même : `overflow-x-auto` n'est pas un
          espacement, `check-exiui` (`planche-espacement`) le laisse passer. Plus de wrapper
          `p-1 pb-2` : il logeait la réserve de padding de la planche D bornée (1.2.1) et des
          équerres supposées hors cadre. En planche B, les équerres (`inset: -2px`) tombent
          sur le bord extérieur du cadre de chaque colonne, donc dans la zone visible. */}
      <div
        className={`${CELLS_PLANCHE} grid-flow-col auto-cols-[minmax(15rem,1fr)] overflow-x-auto`}
      >
        {columns.map((column) => {
          const columnTasks = grouped.get(column.id) ?? [];
          const overLimit = Boolean(column.wipLimit) && columnTasks.length > column.wipLimit;
          const isDropTarget = previewColumnId === column.id;
          return (
            <section
              key={column.id}
              className={`${CELL_PLANCHE} flex flex-col ${isDropTarget ? 'bg-accent-wash' : 'bg-paper'}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={canMove ? onDrop(column.id) : undefined}
            >
              {/* Équerres creuses aux quatre coins DE CETTE colonne — la poignée de sélection
                  du système (exiUI, reference.md §11.7, planche B). */}
              <span className="ticks" aria-hidden="true" />
              <header className="flex items-center justify-between border-b-2 border-line px-3 py-2">
                <span className="font-mono text-[0.74rem] uppercase tracking-eyebrow text-ink">
                  {column.name}
                </span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px] text-ink-faint">
                    {columnTasks.length}
                    {column.wipLimit ? ` / ${column.wipLimit}` : ''}
                  </span>
                  {onCreateTask && (
                    <button
                      type="button"
                      className={BTN_ICON}
                      // Le libellé accessible nomme la colonne : « + » seul se répète à
                      // l'identique sur chaque colonne et ne dit rien au lecteur d'écran.
                      aria-label={t('kanban.addTaskAria', { column: column.name })}
                      onClick={() => onCreateTask(column.id)}
                    >
                      +
                    </button>
                  )}
                </div>
              </header>
              {overLimit && (
                <p className="border-b-2 border-accent-trait bg-accent-wash px-3 py-1 text-[11px] uppercase tracking-label text-accent-ink">
                  {t('kanban.wipLimitExceeded')}
                </p>
              )}

              {columnTasks.length === 0 ? (
                // Cellule vide : hachure -45°, jamais un simple espace blanc (règle
                // « hachure = vide », exiUI reference.md §2.6/§6.7).
                <div className="hatch flex flex-1 items-center justify-center p-4">
                  <p className="bg-paper px-2 text-center text-xs text-ink-faint">
                    {t('kanban.noTask')}
                  </p>
                </div>
              ) : (
                <div className="flex flex-1 flex-col divide-y-2 divide-line">
                  {columnTasks.map((task) => {
                    const picked = pickedTaskId === task.id;
                    return (
                      <article
                        key={task.id}
                        role="button"
                        tabIndex={0}
                        draggable={canMove}
                        data-picked={picked || undefined}
                        aria-pressed={canMove ? picked : undefined}
                        onDragStart={(e) => e.dataTransfer.setData('text/plain', task.id)}
                        onKeyDown={onCardKeyDown(task)}
                        onClick={() => onOpenTask(task.id)}
                        className={`p-2 text-left text-sm text-ink hover:bg-line-soft ${
                          canMove ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
                        } ${picked ? 'bg-accent-wash' : 'bg-paper'}`}
                      >
                        {task.parentId && phaseNameById.has(task.parentId) && (
                          <p className="font-mono text-[10px] uppercase tracking-label text-ink-faint">
                            {phaseNameById.get(task.parentId)}
                          </p>
                        )}
                        <div className="flex items-center gap-1.5">
                          {canMove && (
                            <span
                              data-grip
                              aria-hidden="true"
                              className="grip h-3 w-1.5 shrink-0"
                            />
                          )}
                          <p className="font-mono text-[10px] uppercase tracking-label text-ink-faint">
                            {taskReference(projectKey ?? '…', task.number)}
                          </p>
                        </div>
                        <p className="mt-1 font-semibold">{task.title}</p>
                        {/* Barre d'avancement pleine largeur (222:6129, décision FINALE
                              228:32911) : piste + remplissage à l'accent, pourcentage au bout
                              sur la même ligne. Aucune barre pour un jalon — un jalon n'a pas
                              d'avancement propre (§ audit exiUI). Visible aussi dans Terminé :
                              une tâche déposée là a `progress` forcé à 100 côté serveur
                              (`tasks.service.js`), la barre le confirme au lieu de disparaître. */}
                        {task.kind !== 'milestone' && (
                          <div className="mt-1.5">
                            <Progress value={task.progress} />
                          </div>
                        )}
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                          {/* Le statut n'est porté que s'il DIVERGE de celui qu'applique la
                              colonne : répéter « En cours » sur chaque carte de la colonne
                              « En cours » est du bruit, alors qu'une tâche bloquée posée là, ou
                              n'importe quelle carte d'une colonne qui ne pilote pas le statut,
                              est exactement ce qu'il faut voir. */}
                          {task.status !== column.status && <StatusTag status={task.status} />}
                          <PriorityTag priority={task.priority} />
                          {task.assignee && (
                            <span className="text-[11px] text-ink-faint">{task.assignee.name}</span>
                          )}
                        </div>
                        {picked && (
                          <p className="mt-1.5 font-mono text-[10px] uppercase tracking-label text-accent-ink">
                            {t('kanban.moveTarget', {
                              column: columnNameById.get(previewColumnId) ?? '…',
                            })}
                          </p>
                        )}
                      </article>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
